/**
 * Reports API
 */

import { Report, ReportJobRequest, ReportJobStatus } from '../types/api';
import { getAPIClient } from './client';

export async function listReports(
  projectId: string,
  options?: {
    exclude_report_type?: string;
    report_type?: string;
    status?: string;
    source_type?: string;
  }
): Promise<Report[]> {
  const client = getAPIClient();
  const params: any = { project_id: projectId };
  if (options?.exclude_report_type) {
    params.exclude_report_type = options.exclude_report_type;
  }
  if (options?.report_type) {
    params.report_type = options.report_type;
  }
  if (options?.status) {
    params.status = options.status;
  }
  if (options?.source_type) {
    params.source_type = options.source_type;
  }
  return client.get<Report[]>('/reports', { params });
}

export async function getReport(reportId: string): Promise<Report> {
  const client = getAPIClient();
  return client.get<Report>(`/reports/${reportId}`);
}

export async function submitReportJob(request: ReportJobRequest): Promise<ReportJobStatus> {
  const client = getAPIClient();
  console.log('[API] Submitting report job:', request);
  try {
    const response = await client.post<ReportJobStatus>('/reports/jobs', request);
    console.log('[API] Report job response:', response);
    return response;
  } catch (error: any) {
    console.error('[API] Report job failed:', error);
    console.error('[API] Error response:', error.response?.data);
    throw error;
  }
}

export async function getReportJobStatus(jobId: string): Promise<ReportJobStatus> {
  const client = getAPIClient();
  return client.get<ReportJobStatus>(`/reports/jobs/${jobId}/status`);
}

export async function listReportJobs(projectId: string): Promise<ReportJobStatus[]> {
  const client = getAPIClient();
  // Fetch all jobs for the project
  const jobs = await client.get<any[]>('/jobs', { 
    params: { 
      project_id: projectId
    } 
  });
  
  console.log(`[listReportJobs] Fetched ${jobs.length} total jobs for project ${projectId}`);
  
  // Filter to only include report generation jobs that aren't completed
  // Include 'checkpointed' status as these are still in-progress
  const reportJobs = jobs
    .filter(job => {
      const isReportGen = job.type === 'report_generation';
      const isInProgress = ['pending', 'queued', 'running', 'checkpointed'].includes(job.status);
      if (isReportGen && !isInProgress) {
        console.log(`[listReportJobs] Excluding completed report job ${job.id} with status ${job.status}`);
      }
      return isReportGen && isInProgress;
    })
    .map(job => {
      const reportType = job.input_data?.report_type || job.input_data?.metadata?.frontend_type || 'unknown';
      console.log(`[listReportJobs] Including report job ${job.id}: type=${reportType}, status=${job.status}`);
      
      return {
        ...job,
        // Ensure progress_data exists and is properly structured
        progress_data: job.progress_data || {
          questions_complete: 0,
          total_questions: 0,
          sections_complete: 0,
          cache_hits: 0,
          cache_misses: 0,
          eta_seconds: null,
          current_section: null,
          current_question: null,
          status_message: job.stage_message || `Status: ${job.status}`,
        }
      };
    }) as ReportJobStatus[];
  
  console.log(`[listReportJobs] Returning ${reportJobs.length} in-progress report jobs`);
  return reportJobs;
}

export async function downloadReportV1(reportId: string): Promise<string> {
  const client = getAPIClient();
  return client.get<string>(`/reports/${reportId}/v1.md`, {
    responseType: 'text',
  } as any);
}

export async function downloadReportV2(reportId: string, view: boolean = true): Promise<string> {
  const client = getAPIClient();
  return client.get<string>(`/reports/${reportId}/v2.md`, {
    responseType: 'text',
    params: { view: view ? 'true' : 'false' },
  } as any);
}

export async function downloadReportPDF(reportId: string): Promise<Blob> {
  const client = getAPIClient();
  return client.get<Blob>(`/reports/${reportId}/pdf`, {
    responseType: 'blob',
  });
}

export async function downloadReportDOCX(reportId: string): Promise<Blob> {
  const client = getAPIClient();
  return client.get<Blob>(`/reports/${reportId}/docx`, {
    responseType: 'blob',
  });
}

export async function downloadReportXLSX(reportId: string): Promise<Blob> {
  const client = getAPIClient();
  return client.get<Blob>(`/reports/${reportId}/xlsx`, {
    responseType: 'blob',
  });
}

// SSE connection for real-time progress
export function streamReportProgress(jobId: string): EventSource {
  const client = getAPIClient();
  const baseURL = (client as any).client.defaults.baseURL;
  const apiKey = (client as any).apiKey;
  
  const url = `${baseURL}/reports/jobs/${jobId}/stream`;
  const eventSource = new EventSource(url, {
    withCredentials: false,
  });
  
  return eventSource;
}

// Report versioning
export async function getReportVersions(reportId: string): Promise<any[]> {
  const client = getAPIClient();
  return client.get<any[]>(`/reports/${reportId}/versions`);
}

export async function getReportVersion(reportId: string, version: number): Promise<string> {
  const client = getAPIClient();
  return client.get<string>(`/reports/${reportId}/versions/${version}`, {
    responseType: 'text',
  } as any);
}

export async function restoreReportVersion(reportId: string, version: number): Promise<Report> {
  const client = getAPIClient();
  return client.post<Report>(`/reports/${reportId}/versions/${version}/restore`);
}

// Report collaboration
export interface ReportComment {
  id: string;
  report_id: string;
  user_id: string;
  user_name: string;
  content: string;
  section?: string;
  resolved: boolean;
  parent_comment_id?: string;  // For threading
  comment_type?: 'suggestion' | 'correction' | 'clarification' | 'compliance_issue' | 'quality_concern' | 'general';
  priority?: 'low' | 'medium' | 'high';
  created_at: string;
  updated_at: string;
}

export type CommentType = 'suggestion' | 'correction' | 'clarification' | 'compliance_issue' | 'quality_concern' | 'general';
export type CommentPriority = 'low' | 'medium' | 'high';

export async function getReportComments(
  reportId: string,
  commentType?: CommentType,
  priority?: CommentPriority,
  section?: string
): Promise<ReportComment[]> {
  const client = getAPIClient();
  return client.get<ReportComment[]>(`/reports/${reportId}/comments`, {
    params: {
      ...(commentType && { comment_type: commentType }),
      ...(priority && { priority }),
      ...(section && { section }),
    },
  });
}

export async function addReportComment(
  reportId: string,
  content: string,
  section?: string,
  parentCommentId?: string,
  commentType?: CommentType,
  priority?: CommentPriority
): Promise<ReportComment> {
  const client = getAPIClient();
  return client.post<ReportComment>(`/reports/${reportId}/comments`, {
    content,
    section,
    parent_comment_id: parentCommentId,
    comment_type: commentType,
    priority,
  });
}

export async function resolveReportComment(
  reportId: string,
  commentId: string
): Promise<ReportComment> {
  const client = getAPIClient();
  return client.patch<ReportComment>(`/reports/${reportId}/comments/${commentId}/resolve`);
}

// Enhanced comment functions
export async function getCommentThreads(reportId: string): Promise<ReportComment[]> {
  // Get all comments and group by thread (parent_comment_id)
  const comments = await getReportComments(reportId);
  return comments;
}

export async function getCommentsByType(
  reportId: string,
  type: CommentType
): Promise<ReportComment[]> {
  return getReportComments(reportId, type);
}

export async function getCommentsByPriority(
  reportId: string,
  priority: CommentPriority
): Promise<ReportComment[]> {
  return getReportComments(reportId, undefined, priority);
}

export async function updateComment(
  commentId: string,
  reportId: string,
  updates: {
    content?: string;
    comment_type?: CommentType;
    priority?: CommentPriority;
    resolved?: boolean;
  }
): Promise<ReportComment> {
  // Note: Backend doesn't have a direct update endpoint yet, but we can extend it
  // For now, this is a placeholder that would need backend support
  const client = getAPIClient();
  // This would require a PATCH /reports/{reportId}/comments/{commentId} endpoint
  throw new Error('Update comment endpoint not yet implemented. Use addReportComment for replies.');
}

// Report sharing
export interface ReportShare {
  report_id: string;
  user_id: string;
  user_email: string;
  permission: 'view' | 'edit' | 'admin';
  created_at: string;
}

export async function shareReport(
  reportId: string,
  userEmails: string[],
  permission: 'view' | 'edit' | 'admin' = 'view'
): Promise<void> {
  const client = getAPIClient();
  return client.post(`/reports/${reportId}/share`, {
    share_with: userEmails,  // Backend expects 'share_with' not 'user_emails'
    permission,
  });
}

export async function unshareReport(reportId: string, userEmails: string[]): Promise<void> {
  const client = getAPIClient();
  return client.post(`/reports/${reportId}/unshare`, {
    remove_users: userEmails,  // Backend expects 'remove_users' not 'user_emails'
  });
}

export async function getReportShares(reportId: string): Promise<ReportShare[]> {
  const client = getAPIClient();
  return client.get<ReportShare[]>(`/reports/${reportId}/shares`);
}

// Report analytics
export interface ReportAnalytics {
  report_id: string;
  views: number;
  downloads: number;
  shares: number;
  avg_time_spent_seconds: number;
  last_viewed_at: string | null;
  viewers: Array<{
    user_id: string;
    user_name: string;
    view_count: number;
    last_viewed_at: string;
  }>;
}

export async function getReportAnalytics(reportId: string): Promise<ReportAnalytics> {
  const client = getAPIClient();
  return client.get<ReportAnalytics>(`/reports/${reportId}/analytics`);
}

export async function trackReportView(reportId: string): Promise<void> {
  const client = getAPIClient();
  return client.post(`/reports/${reportId}/track-view`);
}

export async function trackReportDownload(reportId: string, format: string): Promise<void> {
  const client = getAPIClient();
  return client.post(`/reports/${reportId}/track-download`, { format });
}

// Report regeneration
export async function regenerateReportSections(
  reportId: string,
  sectionIndices: number[]
): Promise<Report> {
  const client = getAPIClient();
  return client.patch<Report>(`/reports/${reportId}/regenerate`, {
    section_indices: sectionIndices,
  });
}

// Add evidence to address gaps
export async function addEvidenceToReport(
  reportId: string,
  artifactIds: string[]
): Promise<Report> {
  const client = getAPIClient();
  return client.post<Report>(`/reports/${reportId}/add-evidence`, {
    artifact_ids: artifactIds,
  });
}

// Update report status
export async function updateReportStatus(
  reportId: string,
  status: 'draft' | 'in_review' | 'approved' | 'submitted' | 'archived'
): Promise<Report> {
  const client = getAPIClient();
  return client.patch<Report>(`/reports/${reportId}/status`, { status });
}

// Remove citation from report
export interface RemoveCitationRequest {
  artifact_id: string;
  section_id?: string;
}

export async function removeCitation(
  reportId: string,
  request: RemoveCitationRequest
): Promise<{ status: string; report_id: string; artifact_id: string; citations_removed: number; updated_by: string }> {
  const client = getAPIClient();
  return client.post(`/reports/${reportId}/citations/remove`, request);
}

// Regenerate sections with excluded artifacts
export interface RegenerateSectionsRequest {
  section_ids: string[];
  excluded_artifact_ids: string[];
}

export interface RegenerateSectionsResponse {
  status: string;
  report_id: string;
  sections_regenerated: number;
  excluded_artifacts: number;
  new_stats: {
    total_questions: number;
    answered: number;
    avg_confidence: number;
    total_citations: number;
    evidence_gaps: number;
  };
  updated_by: string;
}

export async function regenerateSections(
  reportId: string,
  request: RegenerateSectionsRequest
): Promise<RegenerateSectionsResponse> {
  const client = getAPIClient();
  return client.post<RegenerateSectionsResponse>(`/reports/${reportId}/sections/regenerate`, request);
}

// Compare reports
export interface ReportComparison {
  report1_id: string;
  report2_id: string;
  sections_changed: Array<{
    section: string;
    change_type: 'added' | 'removed' | 'modified';
    diff: string;
  }>;
  citations_changed: number;
  confidence_delta: number;
}

export async function compareReports(
  reportId1: string,
  reportId2: string
): Promise<ReportComparison> {
  const client = getAPIClient();
  return client.post<ReportComparison>('/reports/compare', {
    report_id_1: reportId1,
    report_id_2: reportId2,
  });
}

// Update report fields (name, etc.)
export async function updateReport(
  reportId: string,
  updates: { name?: string; client_name?: string }
): Promise<{ report_id: string; updated_fields: string[]; name: string; client_name: string }> {
  const client = getAPIClient();
  return client.patch(`/reports/${reportId}`, updates);
}

// Get job associated with report
export async function getReportJob(reportId: string): Promise<ReportJobStatus> {
  const client = getAPIClient();
  return client.get<ReportJobStatus>(`/reports/${reportId}/job`);
}

// Resume stuck report generation job
export async function resumeReportJob(reportId: string): Promise<ReportJobStatus> {
  const client = getAPIClient();
  return client.post<ReportJobStatus>(`/reports/${reportId}/job/resume`);
}

// Save report content as new version
export async function saveReportContent(
  reportId: string,
  content: string,
  versionType: 'v1' | 'v2' | 'v3' | 'v4' | string = 'v2',
  changeSummary?: string
): Promise<{ report_id: string; version: string; current_version: number; name: string; updated_at: string }> {
  const client = getAPIClient();
  return client.post(`/reports/${reportId}/save-content`, { 
    content, 
    version_type: versionType,
    change_summary: changeSummary || 'Manual edit via editor'
  });
}

// Custom Sections API Functions
export interface AddCustomSectionRequest {
  section_name: string;
  section_type?: 'standard' | 'custom' | 'appendix' | 'table' | 'figure';
  content_mode: 'ai' | 'manual';
  question?: string;  // Required if content_mode='ai'
  manual_content?: string;  // Required if content_mode='manual'
  position?: number;  // Optional: insert at specific position (0-based)
  metadata?: Record<string, any>;
}

export interface UpdateCustomSectionRequest {
  section_name?: string;
  answer?: string;  // For manual sections
  question?: string;  // For AI sections (triggers regeneration)
  regenerate?: boolean;  // Regenerate content (only for AI sections)
  metadata?: Record<string, any>;
}

export interface SectionOrder {
  section_id: string;
  display_order: number;
}

export interface ReorderSectionsRequest {
  section_orders: SectionOrder[];
}

export interface SectionImportItem {
  section_id: string;
  import_mode: 'copy_content' | 'regenerate_content';
  copy_citations?: boolean;  // Only if import_mode='copy_content'
  new_section_name?: string;
}

export interface ImportSectionsRequest {
  source_report_id: string;
  sections_to_import: SectionImportItem[];
  position?: number;  // Optional: insert at specific position
}

export interface GetSectionsResponse {
  sections: ReportSection[];
  total: number;
  custom_count: number;
}

export interface ImportSectionsResponse {
  status: string;
  imported_sections: ReportSection[];
  total_imported: number;
}

/**
 * Add a custom section to a report
 */
export async function addCustomSection(
  reportId: string,
  request: AddCustomSectionRequest
): Promise<ReportSection> {
  const client = getAPIClient();
  return client.post<ReportSection>(`/reports/${reportId}/sections/custom`, request);
}

/**
 * Get all sections for a report
 */
export async function getReportSections(
  reportId: string,
  options?: { includeCustom?: boolean; orderBy?: 'display_order' | 'created_at' | 'section_name' }
): Promise<GetSectionsResponse> {
  const client = getAPIClient();
  return client.get<GetSectionsResponse>(`/reports/${reportId}/sections`, {
    params: {
      include_custom: options?.includeCustom ?? true,
      order_by: options?.orderBy ?? 'display_order',
    },
  });
}

/**
 * Update a custom section
 */
export async function updateCustomSection(
  reportId: string,
  sectionId: string,
  request: UpdateCustomSectionRequest
): Promise<ReportSection> {
  const client = getAPIClient();
  return client.patch<ReportSection>(`/reports/${reportId}/sections/${sectionId}`, request);
}

/**
 * Delete a custom section
 */
export async function deleteCustomSection(
  reportId: string,
  sectionId: string
): Promise<void> {
  const client = getAPIClient();
  return client.delete(`/reports/${reportId}/sections/${sectionId}`);
}

/**
 * Reorder sections (drag-and-drop)
 */
export async function reorderSections(
  reportId: string,
  request: ReorderSectionsRequest
): Promise<{ status: string; updated_count: number }> {
  const client = getAPIClient();
  return client.post(`/reports/${reportId}/sections/reorder`, request);
}

/**
 * Import sections from a previous report
 */
export async function importSections(
  reportId: string,
  request: ImportSectionsRequest
): Promise<ImportSectionsResponse> {
  const client = getAPIClient();
  return client.post<ImportSectionsResponse>(`/reports/${reportId}/sections/import`, request);
}

// Proposal-specific functions (using report infrastructure)
export interface ProposalTemplate {
  sections: Array<{ name: string; description: string }>;
  default_questions: Array<{ section: string; question: string }>;
  template_source: string;
  entity_types_by_question?: Record<string, string[]>; // Maps question text to entity types that will be queried
}

export interface ProposalTemplateStreamStatusData {
  stage: 
    | 'started' 
    | 'retrieval' 
    | 'retrieval_complete' 
    | 'document_parsing'
    | 'structure_extraction'
    | 'section_mapping'
    | 'question_generation'
    | 'keyword_extraction'
    | 'stats_analysis'
    | 'compliance_matrix_extraction'
    | 'parsing' 
    | 'fallback' 
    | 'complete';
  message: string;
  elapsed?: number;
  evidence_count?: number;
  retrieval_time?: number;
  progress?: number; // Progress percentage 0-100
}

export interface ProposalTemplateStreamCompleteData {
  sections: Array<{ name: string; description: string }>;
  default_questions: Array<{ section: string; question: string }>;
  template_source: string;
  retrieval_time?: number;
  total_time?: number;
  entity_types_by_question?: Record<string, string[]>; // Maps question text to entity types that will be queried
}

export interface ProposalTemplateStreamErrorData {
  message: string;
  code: string;
  elapsed?: number;
}

/**
 * Streaming Proposal Template Extraction using Server-Sent Events (SSE)
 * 
 * This uses the existing RAG streaming endpoint internally - no redundancy.
 * The backend endpoint is just a thin wrapper that formats the opportunity
 * into a RAG query and parses the response.
 */
export function getProposalTemplateStream(
  opportunityId: string,
  projectId: string,
  callbacks: {
    onStatus?: (data: ProposalTemplateStreamStatusData) => void;
    onComplete?: (data: ProposalTemplateStreamCompleteData) => void;
    onError?: (data: ProposalTemplateStreamErrorData) => void;
    forceRegenerate?: boolean;  // Force regeneration, bypassing cache
  },
  artifactIds?: string[]  // Optional artifact IDs to pass as query parameter
): AbortController {
  const controller = new AbortController();
  
  // Track if we've already closed due to error to prevent infinite retries
  let hasErrored = false;
  
  (async () => {
    try {
      const { getAPIBaseUrl, getAuthHeaders } = await import('./client');
      const baseUrl = getAPIBaseUrl();
      const headers = getAuthHeaders();
      
      // Use the existing RAG streaming endpoint wrapper
      // The backend endpoint delegates to /rag/query/stream internally
      const url = new URL(`${baseUrl}/api/opportunities/${opportunityId}/proposal-template/stream`);
      url.searchParams.set('project_id', projectId);
      if (artifactIds && artifactIds.length > 0) {
        url.searchParams.set('artifact_ids', artifactIds.join(','));
      }
      if (callbacks.forceRegenerate) {
        url.searchParams.set('force_regenerate', 'true');
      }
      if (headers['Authorization']) {
        url.searchParams.set('token', headers['Authorization'].replace('Bearer ', ''));
      }
      
      // EventSource doesn't support custom headers, so token is passed in query
      // Set withCredentials to false to match backend CORS config
      const eventSource = new EventSource(url.toString(), {
        withCredentials: false, // Must match backend CORS allow_credentials setting
      } as any);
      
      // Handle abort
      controller.signal.addEventListener('abort', () => {
        hasErrored = true;
        eventSource.close();
      });
      
      // Handle incremental section streaming
      const receivedSections: Array<{ name: string; description: string }> = [];
      const receivedQuestions: Array<{ section: string; question: string }> = [];
      
      eventSource.addEventListener('section', (event: MessageEvent) => {
        try {
          if (!event.data) return;
          const sectionData = JSON.parse(event.data);
          if (sectionData.name) {
            receivedSections.push({
              name: sectionData.name,
              description: sectionData.description || '',
            });
            // Update state incrementally if callback supports it
            if (callbacks.onStatus) {
              callbacks.onStatus({
                stage: 'parsing',
                message: `Processing section ${receivedSections.length}/${sectionData.total_sections || receivedSections.length}: ${sectionData.name}`,
              });
            }
            console.log('[ProposalTemplateStream] Received section:', sectionData.name, `(${receivedSections.length} total)`);
          }
        } catch (e) {
          console.warn('[ProposalTemplateStream] Failed to parse section event:', e);
        }
      });
      
      // Handle incremental question streaming
      eventSource.addEventListener('question', (event: MessageEvent) => {
        try {
          if (!event.data) return;
          const questionData = JSON.parse(event.data);
          if (questionData.section && questionData.question) {
            receivedQuestions.push({
              section: questionData.section,
              question: questionData.question,
            });
            // Update state incrementally if callback supports it
            if (callbacks.onStatus) {
              callbacks.onStatus({
                stage: 'parsing',
                message: `Generated question ${questionData.questions_streamed || receivedQuestions.length}/${questionData.total_questions || receivedQuestions.length} for section: ${questionData.section}`,
              });
            }
            if (receivedQuestions.length % 10 === 0) {
              console.log('[ProposalTemplateStream] Received questions:', receivedQuestions.length, `(latest: ${questionData.section})`);
            }
          }
        } catch (e) {
          console.warn('[ProposalTemplateStream] Failed to parse question event:', e);
        }
      });
      
      // Map RAG stream events to proposal template events
      eventSource.addEventListener('status', (event: MessageEvent) => {
        try {
          if (!event.data) return;
          const data = JSON.parse(event.data);
          
          // Map RAG stages to proposal template stages
          // Handle all intermediate stages for better progress tracking
          let mappedStage: ProposalTemplateStreamStatusData['stage'];
          if (data.stage === 'started' || data.stage === 'context') {
            mappedStage = 'started';
          } else if (data.stage === 'retrieval') {
            mappedStage = 'retrieval';
          } else if (data.stage === 'retrieval_complete') {
            mappedStage = 'retrieval_complete';
          } else if (data.stage === 'generating' || data.stage === 'thinking') {
            mappedStage = 'parsing';
          } else if (data.stage === 'complete') {
            mappedStage = 'complete';
          } else if (data.stage === 'no_evidence') {
            // This will trigger fallback to policy pack
            mappedStage = 'fallback';
          } else {
            mappedStage = data.stage as any;
          }
          
          callbacks.onStatus?.({
            stage: mappedStage,
            message: data.message,
            elapsed: data.elapsed,
            evidence_count: data.evidence_count,
            retrieval_time: data.retrieval_time,
          });
        } catch (e) {
          console.error('[ProposalTemplateStream] Failed to parse status event:', e);
        }
      });
      
      // Handle 'progress' events from template generation worker
      eventSource.addEventListener('progress', (event: MessageEvent) => {
        try {
          if (!event.data) return;
          const data = JSON.parse(event.data);
          
          console.log('[ProposalTemplateStream] Progress event:', data);
          
          // Forward to status callback with the stage as-is
          callbacks.onStatus?.({
            stage: data.stage as any,
            message: data.message || '',
            elapsed: data.elapsed,
            progress: data.progress,
          });
          
          // Check if this is a completion stage (structure_extraction_complete, awaiting_approval)
          // These indicate Phase 1 of template generation is complete
          if (data.stage === 'structure_extraction_complete' || data.stage === 'awaiting_approval') {
            console.log('[ProposalTemplateStream] Structure extraction complete, treating as completion');
            // We don't have the template data in the progress event, so we'll let the 'complete' event handler fetch it
            // The backend should send a 'complete' event after this, but if it doesn't, we need to handle it
            // For now, we'll rely on the frontend to detect this stage and fetch the template
          }
        } catch (e) {
          console.error('[ProposalTemplateStream] Failed to parse progress event:', e);
        }
      });
      
      eventSource.addEventListener('complete', (event: MessageEvent) => {
        if (hasErrored) {
          return; // Already handled, don't process
        }
        
        try {
          if (!event.data) {
            hasErrored = true;
            // Empty response - fallback to policy pack
            callbacks.onComplete?.({
              sections: [],
              default_questions: [],
              template_source: 'policy_pack_fallback',
              retrieval_time: 0,
              total_time: 0,
              entity_types_by_question: {},
            });
            eventSource.close();
            return;
          }
          
          const rawData = JSON.parse(event.data);
          
          // Check if this is direct template data (Phase 2) or wrapped RAG response (old flow)
          let templateData: any = null;
          let answer = '';
          
          if (rawData.sections && rawData.default_questions) {
            // NEW FLOW: Direct template data from worker (Phase 2)
            console.log('[ProposalTemplateStream] Direct template data received from worker');
            templateData = rawData;
          } else if (rawData.answer) {
            // OLD FLOW: RAG response wrapper
            console.log('[ProposalTemplateStream] RAG wrapper detected, parsing answer field');
            answer = rawData.answer || '';
            
            // Try to extract JSON from answer
            try {
              // First try to parse as direct JSON (backend sends JSON string)
              try {
                templateData = JSON.parse(answer);
              } catch {
                // If that fails, try to extract from markdown code blocks
                const jsonMatch = answer.match(/```(?:json)?\s*(\{.*?\})\s*```/s) || answer.match(/\{.*\}/s);
                if (jsonMatch) {
                  const jsonStr = jsonMatch[1] || jsonMatch[0];
                  templateData = JSON.parse(jsonStr);
                }
              }
            } catch (parseError) {
              console.warn('[ProposalTemplateStream] Failed to parse answer field:', parseError);
            }
          }
          
          // CRITICAL: Use received sections/questions from incremental streaming if available
          // This ensures we have the actual data even if JSON parsing fails
          const finalSections = (receivedSections.length > 0 ? receivedSections : templateData?.sections) || [];
          const finalQuestions = (receivedQuestions.length > 0 ? receivedQuestions : templateData?.default_questions) || [];
          
          if (finalSections.length > 0 || finalQuestions.length > 0) {
            // Valid template data found
            console.log('[ProposalTemplateStream] Complete event received:', {
              sections_from_stream: receivedSections.length,
              questions_from_stream: receivedQuestions.length,
              sections_from_json: templateData?.sections?.length || 0,
              questions_from_json: templateData?.default_questions?.length || 0,
              final_sections: finalSections.length,
              final_questions: finalQuestions.length,
            });
            
            callbacks.onComplete?.({
              sections: finalSections,
              default_questions: finalQuestions,
              template_source: templateData?.template_source || rawData.template_source || (rawData.status === 'fallback' ? 'policy_pack_fallback' : 'rag_analysis_with_policy_pack'),
              retrieval_time: rawData.retrieval_time || 0,
              total_time: rawData.total_time || 0,
              entity_types_by_question: templateData?.entity_types_by_question || rawData.entity_types_by_question || {},
            });
          } else {
            // No valid template in answer - this shouldn't happen with new backend
            // but keep as fallback
            console.warn('[ProposalTemplateStream] No valid template data in answer, using empty template');
            callbacks.onComplete?.({
              sections: [],
              default_questions: [],
              template_source: 'policy_pack_fallback',
              retrieval_time: rawData.retrieval_time || 0,
              total_time: rawData.total_time || 0,
              entity_types_by_question: {},
            });
          }
          
          eventSource.close();
        } catch (e) {
          if (!hasErrored) {
            hasErrored = true;
            // Even on parse error, try to return fallback
            callbacks.onComplete?.({
              sections: [],
              default_questions: [],
              template_source: 'policy_pack_fallback',
              retrieval_time: 0,
              total_time: 0,
              entity_types_by_question: {},
            });
          }
          eventSource.close();
        }
      });
      
      eventSource.addEventListener('error', (event: MessageEvent) => {
        if (hasErrored) {
          return; // Already handled
        }
        hasErrored = true;
        
        try {
          if (event.data) {
            const data = JSON.parse(event.data);
            callbacks.onError?.(data);
          } else {
            callbacks.onError?.({
              message: 'Stream error',
              code: 'STREAM_ERROR',
            });
          }
        } catch (e) {
          callbacks.onError?.({
            message: 'Failed to parse error response',
            code: 'PARSE_ERROR',
          });
        }
        eventSource.close();
      });
      
      eventSource.onerror = (error) => {
        // Prevent infinite retries - EventSource auto-reconnects by default
        if (hasErrored) {
          return; // Already handled, don't retry
        }
        
        hasErrored = true;
        
        // EventSource doesn't provide detailed error info
        // Check readyState to determine error type
        if (eventSource.readyState === EventSource.CLOSED) {
          callbacks.onError?.({
            message: 'Connection closed',
            code: 'CONNECTION_CLOSED',
          });
        } else {
          // Check if it's a CORS error
          const isCorsError = eventSource.readyState === EventSource.CONNECTING;
          callbacks.onError?.({
            message: isCorsError ? 'CORS error - check server configuration' : 'Connection error',
            code: isCorsError ? 'CORS_ERROR' : 'CONNECTION_ERROR',
          });
        }
        eventSource.close();
      };
      
    } catch (error: any) {
      callbacks.onError?.({
        message: error.message || 'Failed to start stream',
        code: 'INIT_ERROR',
      });
    }
  })();
  
  return controller;
}

export async function getProposalTemplate(
  opportunityId: string,
  projectId: string
): Promise<ProposalTemplate> {
  const client = getAPIClient();
  return client.get<ProposalTemplate>(
    `/api/opportunities/${opportunityId}/proposal-template`,
    { params: { project_id: projectId } }
  );
}

export interface WorkplanTemplate {
  sections: Array<{ name: string; description: string }>;
  default_questions: Array<{ section: string; question: string }>;
  template_source: string;
  service_line?: string;
  confidence_score: number;
  entity_types_by_question?: Record<string, string[]>;
}

export async function getWorkplanTemplate(
  pastProjectId: string,
  projectId: string,
  serviceLine?: string
): Promise<WorkplanTemplate> {
  const client = getAPIClient();
  return client.post<WorkplanTemplate>(
    `/api/past-projects/${pastProjectId}/workplan-template`,
    {
      project_id: projectId,
      service_line: serviceLine,
      adapt_questions: true,
    }
  );
}

export async function submitProposalJob(
  opportunityId: string,
  projectId: string,
  clientName: string,
  questions: Array<{ section: string; question: string }>,
  metadata?: {
    proposal_name?: string;
    include_past_proposals?: boolean;
    include_evidence?: boolean;
  }
): Promise<ReportJobStatus> {
  const client = getAPIClient();
  
  // Use the standard report job submission
  return submitReportJob({
    project_id: projectId,
    report_type: 'rfp_proposal',
    client_name: clientName,
    questions: questions.map(q => ({
      section: q.section,
      question: q.question,
    })),
    metadata: {
      opportunity_id: opportunityId,
      ...metadata,
    },
    enable_cache: true,
    timeout_seconds: 3600, // 1 hour for proposals
  });
}